import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import {
  ContractOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreVertOutlined,
  UploadFileOutlined,
} from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useRef, useState } from "react"

import type { SalesOrder } from "@/graphql/codegen/graphql"
import { useSalesOrderPermissions } from "@/screens/CustomerOrders/hooks/useSalesOrderPermissions"
import {
  isSalesOrderCancellable,
  isSalesOrderEditable,
  isSalesOrderStatusChangeable,
} from "@/screens/CustomerOrders/utils"

interface SalesOrderRowActionsProps {
  salesOrder: SalesOrder
  onUpload: () => void
  onUpdateStatus: () => void
  onCancel: () => void
}

export const SalesOrderRowActions: FC<SalesOrderRowActionsProps> = ({
  salesOrder,
  onUpload,
  onUpdateStatus,
  onCancel,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { isReadonly } = useSalesOrderPermissions()
  const anchorElRef = useRef<HTMLButtonElement>(null)

  const handleMenuOpen = () => {
    setIsMenuOpen(true)
  }

  const handleMenuClose = () => {
    setIsMenuOpen(false)
  }

  const handleUpload = () => {
    handleMenuClose()
    onUpload()
  }

  const handleUpdateStatus = () => {
    handleMenuClose()
    onUpdateStatus()
  }

  const handleCancel = () => {
    handleMenuClose()
    onCancel()
  }

  const canUpdateStatus = isSalesOrderStatusChangeable(salesOrder.status)
  const canUpload = isSalesOrderEditable(salesOrder.status)
  const canCancel = isSalesOrderCancellable(salesOrder.status)

  return (
    <Box ref={anchorElRef}>
      <IconButton
        size='small'
        onClick={handleMenuOpen}
        aria-label='SO actions'
        data-testid={`customer-orders.so-row.actions-menu-button-${salesOrder.salesOrderId}`}
      >
        <MoreVertOutlined />
      </IconButton>

      {isMenuOpen && (
        <Menu
          anchorEl={anchorElRef.current}
          open
          onClose={handleMenuClose}
          data-testid={`customer-orders.so-row.actions-menu-${salesOrder.salesOrderId}`}
        >
          {canUpdateStatus && (
            <MenuItem
              data-testid='customer-orders.so-row.update-status-menu-item'
              disabled={isReadonly}
              onClick={handleUpdateStatus}
            >
              <ListItemIcon>
                <EditOutlined className='font-light' />
              </ListItemIcon>
              <ListItemText primary='Update Status' />
            </MenuItem>
          )}
          <MenuItem data-testid='customer-orders.so-row.pro-forma-invoice-menu-item' disabled>
            <ListItemIcon>
              <ContractOutlined className='font-light' />
            </ListItemIcon>
            <ListItemText primary='Generate ProForma Invoice' />
          </MenuItem>
          {canUpload && (
            <MenuItem
              data-testid='customer-orders.so-row.upload-menu-item'
              disabled={isReadonly}
              onClick={handleUpload}
            >
              <ListItemIcon>
                <UploadFileOutlined className='font-light' />
              </ListItemIcon>
              <ListItemText primary='Upload Document' />
            </MenuItem>
          )}
          {canCancel && (
            <MenuItem
              data-testid='customer-orders.so-row.cancel-menu-item'
              disabled={isReadonly}
              onClick={handleCancel}
            >
              <ListItemIcon>
                <DeleteOutlined className='font-light' />
              </ListItemIcon>
              <ListItemText primary='Cancel Sales Order' />
            </MenuItem>
          )}
        </Menu>
      )}
    </Box>
  )
}
